import type { GetTeamsLookupBffResponseTeam, NewSpaceResource, Repository, SpaceResource, UserResource } from "@octopusdeploy/octopus-server-client";
import { TeamMultiSelect } from "~/components/MultiSelect/TeamMultiSelect";
import { UserMultiSelect } from "~/components/MultiSelect/UserMultiSelect";
import { required, Text } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
import { WorkflowContent } from "~/workflows/WorkflowContent";
import { workflows } from "~/workflows/workflows";
export async function addSpaceWorkflowLoader(repository: Repository): Promise<AddSpaceWorkflowLoaderData> {
    const users = repository.Users.all();
    const teams = repository.forSystem().Teams.lookupBff();
    return { users: await users, teams: await teams };
}
export interface AddSpaceWorkflowLoaderData {
    users: UserResource[];
    teams: GetTeamsLookupBffResponseTeam[];
}
interface AddSpaceWorkflowProps {
    onSpaceCreated(space: SpaceResource): void;
    loaderData: AddSpaceWorkflowLoaderData | null;
    IO?: (repository: Repository) => {
        createSpace: (space: NewSpaceResource) => Promise<SpaceResource>;
    };
}
const defaultIO = (repository: Repository) => ({
    createSpace: async (space: NewSpaceResource) => await repository.Spaces.create(space),
});
function getInitialModel(): NewSpaceResource {
    return {
        Name: "",
        Slug: "",
        SpaceManagersTeams: [],
        SpaceManagersTeamMembers: [],
        IsDefault: false,
        IsPrivate: false,
        TaskQueueStopped: false,
    };
}
export function AddSpaceWorkflow({ onSpaceCreated, loaderData, IO = defaultIO }: AddSpaceWorkflowProps) {
    const { model, setModel, formProps, createSaveAction } = useForm({
        formName: "Add Space",
        initialModel: getInitialModel(),
        onSubmit: (repository, model) => IO(repository).createSpace(model),
        updateFormModelAfterSubmit: (model) => model,
        afterSubmit: async (_, createdSpace) => {
            onSpaceCreated(createdSpace);
        },
        snackbarSaveText: "New Space created",
    });
    return (<WorkflowContent.Dialog workflow={workflows.addSpaceWorkflow} title="Add New Space" primaryAction={createSaveAction({ enabledWhenClean: true })}>
            <Form {...formProps}>
                <Text value={model.Name} onChange={(Name) => setModel((prev) => ({ ...prev, Name }))} label="New space name" validate={required("Please enter a space name")} autoFocus={true}/>
                <Text value={model.Slug} onChange={(Slug) => setModel((prev) => ({ ...prev, Slug }))} label="New space slug" helperText="Will be generated on save if left blank."/>
                <h4>Select members and teams to be managers of this space</h4>
                <p>The members and teams selected will be added to the Space Manager team and will have management permissions and access to everything in this space. System level concerns of Octopus are outside of this space.</p>
                <UserMultiSelect label={"Select space managers (members)"} items={loaderData?.users ?? []} onChange={(SpaceManagersTeamMembers) => setModel((prev) => ({ ...prev, SpaceManagersTeamMembers }))} value={model.SpaceManagersTeamMembers}/>

                <TeamMultiSelect label={"Select space managers (teams)"} items={loaderData?.teams ?? []} onChange={(SpaceManagersTeams) => setModel((prev) => ({ ...prev, SpaceManagersTeams }))} value={model.SpaceManagersTeams}/>
            </Form>
        </WorkflowContent.Dialog>);
}
